import { useRef, useState } from "react";
import type { ProductCategory } from "@/mocks/products";

interface ProductShowcaseProps {
  categories: ProductCategory[];
}

function CategoryRow({ category, categoryUrl, items }: ProductCategory) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
    setTimeout(checkScroll, 300);
  };

  return (
    <div className="mb-10 md:mb-14">
      <div className="flex items-center justify-between mb-4 md:mb-5">
        <div className="flex items-center gap-3">
          <h3 className="text-lg md:text-2xl font-bold text-foreground-950">{category}</h3>
          <a
            href={categoryUrl}
            target="_blank"
            rel="noopener nofollow"
            className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
          >
            View All <i className="ri-arrow-right-line" />
          </a>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            className="w-8 h-8 md:w-10 md:h-10 rounded-full border border-background-200/80 bg-white flex items-center justify-center text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="ri-arrow-left-s-line text-lg md:text-xl" />
          </button>
          <button
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            className="w-8 h-8 md:w-10 md:h-10 rounded-full border border-background-200/80 bg-white flex items-center justify-center text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <i className="ri-arrow-right-s-line text-lg md:text-xl" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide pb-2"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {items.map((item) => (
          <a
            key={item.url}
            href={item.url}
            target="_blank"
            rel="noopener nofollow"
            className="flex-shrink-0 w-[150px] md:w-[200px] group"
          >
            <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-2 border border-background-200/40">
              <img
                src={item.img}
                alt={item.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <h4 className="text-xs font-medium text-foreground-800 line-clamp-2 mb-0.5 leading-tight group-hover:text-primary-600 transition-colors">
              {item.name}
            </h4>
            <span className="text-sm font-bold text-primary-600">{item.price}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

export default function ProductShowcase({ categories }: ProductShowcaseProps) {
  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-16">
          <span className="inline-block text-xs font-semibold text-primary-600 uppercase tracking-widest mb-3">Products</span>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">Featured Products</h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Browse our curated collection from Kakobuy Spreadsheet. Click any product to view details and purchase on our main site.
          </p>
        </div>
        {categories.map((cat) => (
          <CategoryRow key={cat.category} {...cat} />
        ))}
        <div className="text-center mt-8 md:mt-12">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 bg-primary-500 text-white font-semibold px-6 py-3 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Browse Full Catalog
            <i className="ri-external-link-line" />
          </a>
        </div>
      </div>
    </section>
  );
}